import type { NewMessagesPayload, PullMessagesRequest, PullMessagesResponse, SendMessagesRequest, SendMessagesResponse } from '@proj-airi/server-sdk-shared/v2'
import type { ComputedRef, Ref } from 'vue'

import { defineInvoke } from '@moeru/eventa'
import { createContext as createWsContext, wsErrorEvent } from '@moeru/eventa/adapters/websocket/native'
import { errorMessageFrom } from '@moeru/std'
import { authenticate, newMessages, parseAuthenticateResponse, pullMessages, sendMessages } from '@proj-airi/server-sdk-shared/v2'
import { useWebSocket } from '@vueuse/core'
import { computed, ref, shallowRef, watch } from 'vue'

import * as v from 'valibot'

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
const RECONNECT_RETRIES = -1

/**
 * Server-side auth rejection close code (IANA application range 4000-4999).
 *
 * Browsers swallow the HTTP 401 status when a WebSocket upgrade is rejected,
 * so the only way for the server to distinguish "wrong token, stop retrying"
 * from a transient network drop on the client is to accept the upgrade and
 * close with a custom application code after request authentication fails.
 */
export const WS_CLOSE_UNAUTHORIZED = 4001

// NOTICE:
// The native ws adapter's context type is not directly exported from
// `@moeru/eventa/adapters/websocket/native`; use the inferred return type so
// `ctx.on` / `ctx.emit` overloads stay accurate.
// Source: @moeru/eventa@1.0.0-beta.15 — adapter exports only `createContext` and the
// event constants.
// Removal condition: the adapter exports a public `EventContext` type.
type WsEventContext = ReturnType<typeof createWsContext>['context']

const NewMessagesPayloadSchema = v.object({
  chatId: v.pipe(v.string(), v.minLength(1)),
  fromSeq: v.number(),
  toSeq: v.number(),
  messages: v.array(v.object({
    id: v.pipe(v.string(), v.minLength(1)),
    chatId: v.pipe(v.string(), v.minLength(1)),
    senderId: v.nullable(v.string()),
    role: v.picklist(['system', 'user', 'assistant', 'tool', 'error']),
    content: v.string(),
    seq: v.number(),
    createdAt: v.number(),
    updatedAt: v.number(),
  })),
})

/**
 * WebSocket connection lifecycle states surfaced to the chat-sync layer.
 *
 * - `idle`: never connected, or `disconnect()` was called and we are not
 *   trying to reconnect.
 * - `connecting`: WebSocket handshake in flight (initial or reconnect attempt).
 * - `open`: socket open and `chat:authenticate` succeeded.
 * - `closed`: lost the socket; auto-reconnect may bring it back to `connecting`.
 */
export type ChatWsStatus = 'idle' | 'connecting' | 'open' | 'closed'

/**
 * Disposer returned by `onNewMessages` / `onStatusChange`. Calling it removes
 * the listener; safe to call multiple times.
 */
export type ChatWsUnsubscribe = () => void

export interface CreateChatWsClientOptions {
  /**
   * Base server URL, e.g. `https://api.airi.build`. The client appends
   * `/ws/v2/chat` to build the WebSocket URL.
   */
  serverUrl: string
  /**
   * Resolves the current bearer token at connect/reconnect time. Returning
   * `null` skips connecting (the user is not authenticated).
   */
  getToken: () => string | null
}

export interface ChatWsClient {
  /** Current connection status. Useful for UI banners. */
  status: () => ChatWsStatus
  /** Connect (or reconnect with the latest token). No-op if already open. */
  connect: () => void
  /** Close the socket and stop auto-reconnect until the next `connect()`. The handle is reusable. */
  disconnect: () => void
  /** Permanently dispose the client (stops the status watcher). After `destroy()` the handle is unusable. */
  destroy: () => void
  /** RPC: push messages to a chat. Rejects if disconnected mid-flight. */
  sendMessages: (req: SendMessagesRequest) => Promise<SendMessagesResponse>
  /** RPC: pull messages newer than `afterSeq`. Rejects if disconnected mid-flight. */
  pullMessages: (req: PullMessagesRequest) => Promise<PullMessagesResponse>
  /**
   * Subscribe to inbound `newMessages` push. The handler fires for every
   * authenticated push, including potential echoes of the local sender — the
   * caller MUST dedup by message id.
   */
  onNewMessages: (handler: (payload: NewMessagesPayload) => void) => ChatWsUnsubscribe
  /** Subscribe to status transitions for UI / catchup orchestration. */
  onStatusChange: (handler: (status: ChatWsStatus) => void) => ChatWsUnsubscribe
}

/**
 * Build the `/ws/v2/chat` URL from a base server URL.
 *
 * Before:
 * - "https://api.airi.build"
 *
 * After:
 * - "wss://api.airi.build/ws/v2/chat"
 *
 * The v2 bearer token is sent through `chat:authenticate` after the socket
 * opens, so it must never be serialized into the WebSocket URL.
 *
 * @internal
 */
export function buildChatWsUrl(serverUrl: string): string {
  // Use URL parsing instead of string concat so trailing slashes / paths in
  // serverUrl are normalized cleanly.
  const url = new URL(serverUrl)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/ws/v2/chat`
  url.search = ''
  return url.toString()
}

/**
 * Compute exponential reconnect delay with bounded jitter.
 *
 * Math context: VueUse's autoReconnect supplies `retries` starting at 1 for
 * the first reconnect. The minimum 50% floor keeps the immediate retry from
 * firing in <50ms (a 0..exp uniform jitter previously could fire at ~0ms,
 * producing reconnect storms across many tabs against a hard-down server).
 *
 * @internal
 */
export function computeReconnectDelay(retries: number, baseMs: number, maxMs: number): number {
  const exp = Math.min(maxMs, baseMs * 2 ** Math.max(0, retries - 1))
  // 50% floor + 50% jitter window; total range is [exp/2, exp).
  return Math.floor(exp * 0.5 + Math.random() * exp * 0.5)
}

/**
 * Map VueUse's 3-state status onto the chat-sync 4-state machine.
 *
 * VueUse exposes `OPEN | CONNECTING | CLOSED`. Chat-sync needs to distinguish
 * "never connected / explicitly disconnected" (`idle`) from "lost the socket
 * and auto-reconnect is pending" (`closed`). The caller tracks the user
 * intent via `enabled`; here we just translate the transport state.
 *
 * @internal
 */
export function mapStatus(vue: 'OPEN' | 'CONNECTING' | 'CLOSED', enabled: boolean, authenticated = true): ChatWsStatus {
  if (vue === 'OPEN')
    return authenticated ? 'open' : 'connecting'
  if (vue === 'CONNECTING')
    return 'connecting'
  return enabled ? 'closed' : 'idle'
}

/**
 * Create a chat-sync WebSocket client backed by VueUse's `useWebSocket` plus
 * eventa's native ws adapter for the eventa context that handles RPC and
 * outbound subscription routing.
 *
 * Use when:
 * - The user is signed in and the chat store wants real-time sync.
 *
 * Expects:
 * - `serverUrl` includes scheme (https/http). `getToken()` returns a valid JWT
 *   when the socket opens. The token is sent through `chat:authenticate` after
 *   the WebSocket upgrade.
 *
 * Returns:
 * - A handle exposing connect/disconnect/destroy, RPC functions, and event
 *   hooks. RPC closures resolve the live `EventContext` per invocation so a
 *   reconnect-induced context swap is transparent. In-flight RPCs reject on
 *   disconnect with `chat-ws: rpc cancelled` so callers do not hang
 *   indefinitely. Eventa `1.0.0-beta.15` aborts pending invokes when the
 *   native WebSocket closes.
 */
/**
 * Build the reactive ws URL ref `useWebSocket` watches.
 *
 * `getToken` MUST read from a reactive source (Pinia store ref, Vue ref,
 * computed). The reactive dependency controls whether the client has a token
 * available to authenticate after the socket opens; the token is not part of
 * the URL.
 */
export function createChatWsUrlRef(
  enabled: Ref<boolean>,
  getToken: () => string | null,
  serverUrl: string,
): ComputedRef<string | undefined> {
  return computed(() => {
    if (!enabled.value)
      return undefined
    const token = getToken()
    if (!token)
      return undefined
    return buildChatWsUrl(serverUrl)
  })
}

export function createChatWsClient(options: CreateChatWsClientOptions): ChatWsClient {
  // `enabled` mirrors user intent: connect() flips on, disconnect() flips off.
  // The url ref returns `undefined` when disabled, which makes useWebSocket
  // close cleanly without firing the auto-reconnect loop.
  const enabled = ref(false)
  const tokenRef = computed(options.getToken)
  const urlRef = createChatWsUrlRef(enabled, () => tokenRef.value, options.serverUrl)
  const authenticated = ref(false)
  let authenticationFailures = 0
  let reopenAfterDisconnect = false
  // The socket object is the connection generation. Authentication callbacks
  // must match it before they can update the shared client state.
  let activeSocket: WebSocket | undefined

  // The eventa context is rebuilt on every `onConnected` so RPC + push
  // listeners survive a reconnect by re-binding to the fresh ws.
  // In-flight invokes auto-reject on socket close because the native ws
  // adapter registers wsDisconnectedEvent / wsErrorEvent as abort events on
  // the context (see @moeru/eventa adapters/websocket/native).
  const context = shallowRef<WsEventContext | undefined>(undefined)
  const contextDisposers: Array<() => void> = []
  const newMessagesHandlers = new Set<(payload: NewMessagesPayload) => void>()
  const statusHandlers = new Set<(status: ChatWsStatus) => void>()

  function notifyStatus(next: ChatWsStatus) {
    // Surface every transition in console so v1 reconnect / reconcile traces
    // are greppable; quieter console levels (warn/error) suppress this.
    console.info('[chat-ws] status →', next)
    for (const handler of statusHandlers) {
      try {
        handler(next)
      }
      catch (err) {
        // Listener errors must not poison the status pipeline.
        console.warn('[chat-ws] status handler threw:', errorMessageFrom(err))
      }
    }
  }

  function disposeContext() {
    while (contextDisposers.length > 0) {
      const dispose = contextDisposers.pop()!
      try {
        dispose()
      }
      catch {}
    }
    context.value = undefined
  }

  function attachContextListeners(ctx: WsEventContext) {
    contextDisposers.push(ctx.on(newMessages, (event) => {
      // External boundary: validate the wire payload before fanning it out.
      // A malformed server push would otherwise flow unchecked into every
      // subscriber and into `mergeCloudMessagesIntoSession`.
      const result = v.safeParse(NewMessagesPayloadSchema, event.body)
      if (!result.success) {
        console.warn('[chat-ws] dropped malformed newMessages payload:', result.issues[0]?.message)
        return
      }
      const payload = result.output
      for (const handler of newMessagesHandlers) {
        try {
          handler(payload)
        }
        catch (err) {
          // Same isolation principle as notifyStatus: one bad listener should
          // not silently drop messages for the rest.
          console.warn('[chat-ws] newMessages handler threw:', errorMessageFrom(err))
        }
      }
    }))

    contextDisposers.push(ctx.on(wsErrorEvent, (event) => {
      console.warn('[chat-ws] socket error:', event.body)
    }))
  }

  // The URL ref controls the user connection intent and token presence. The
  // token itself is sent only after the socket opens through Eventa.
  const ws = useWebSocket<string>(urlRef, {
    immediate: false,
    autoClose: true,
    autoReconnect: {
      retries: RECONNECT_RETRIES,
      delay: retries => computeReconnectDelay(
        Math.max(retries, authenticationFailures),
        RECONNECT_BASE_MS,
        RECONNECT_MAX_MS,
      ),
    },
    onConnected(rawWs) {
      activeSocket = rawWs
      const created = createWsContext(rawWs)
      context.value = created.context
      attachContextListeners(created.context)
      authenticated.value = false

      const token = tokenRef.value
      if (!token) {
        rawWs.close()
        return
      }

      void defineInvoke(getAuthenticationContext, authenticate)({ token })
        .then((response) => {
          parseAuthenticateResponse(response)
          if (activeSocket !== rawWs || context.value !== created.context)
            return
          authenticated.value = true
          authenticationFailures = 0
        })
        .catch((error) => {
          if (activeSocket !== rawWs || context.value !== created.context)
            return
          console.warn('[chat-ws] post-connect authentication failed:', errorMessageFrom(error))
          // Close the native socket, not VueUse's wrapper. The wrapper marks
          // this as an explicit disconnect and disables the retry schedule.
          rawWs.close(1011, 'invalid authentication response')
        })
    },
    onDisconnected(rawWs, ev) {
      if (rawWs !== activeSocket)
        return

      const wasAuthenticated = authenticated.value
      const restartForNewToken = reopenAfterDisconnect
      reopenAfterDisconnect = false
      activeSocket = undefined
      disposeContext()
      authenticated.value = false
      if (!wasAuthenticated && enabled.value && ev.code !== WS_CLOSE_UNAUTHORIZED)
        authenticationFailures += 1
      // ROOT CAUSE:
      //
      // useWebSocket's autoReconnect treats every onclose as worth
      // retrying. When the server rejects auth, the only structured
      // signal we get is the close `code` (the close `reason` body is
      // also delivered but not used for routing here). 4001 is our
      // protocol signal for "this token will never succeed without
      // rotation"; calling `ws.close()` here sets
      // useWebSocket's internal `explicitlyClosed` flag so the next
      // onclose path skips the reconnect schedule. A token change below
      // closes the old context and starts a new connection that authenticates
      // with the new token after opening.
      if (restartForNewToken && enabled.value && tokenRef.value) {
        ws.open()
      }
      else if (ev.code === WS_CLOSE_UNAUTHORIZED) {
        console.warn('[chat-ws] server rejected auth (4001), pausing reconnect until token rotates')
        ws.close()
      }
    },
    onError(_rawWs, event) {
      console.warn('[chat-ws] ws error event:', event)
    },
  })

  // Translate VueUse's 3-state status into our 4-state machine and fan it
  // out to the orchestrator. The chat store creates this inside a Pinia
  // setup, which gives us a parent effect scope for `watch` to attach to.
  // NOTICE:
  // We intentionally do NOT stop this watcher in `disconnect()`; previous
  // behavior killed it permanently and any caller that did `disconnect()`
  // followed by `connect()` silently stopped receiving status events. The
  // watcher is idle while the socket is closed, so leaving it attached
  // costs nothing. Use `destroy()` for terminal cleanup.
  const stopStatusWatch = watch(
    [ws.status, enabled, authenticated],
    ([rawStatus, isEnabled, isAuthenticated]) => notifyStatus(mapStatus(rawStatus, isEnabled, isAuthenticated)),
    { immediate: true },
  )

  const stopTokenWatch = watch(tokenRef, (token, previousToken) => {
    if (!enabled.value || !token || !previousToken || token === previousToken)
      return

    authenticated.value = false
    disposeContext()
    if (ws.status.value === 'CLOSED') {
      if (token)
        ws.open()
      return
    }

    // Wait for the old close event before opening its replacement. VueUse owns
    // one status ref, so opening early lets that old close overwrite OPEN.
    reopenAfterDisconnect = token !== null
    ws.close()
  })

  function getContext(): WsEventContext {
    if (!context.value || !authenticated.value)
      throw new Error('chat-ws not authenticated')
    return context.value
  }

  function getAuthenticationContext(): WsEventContext {
    if (!context.value)
      throw new Error('chat-ws not connected')
    return context.value
  }

  // We pass a function so each invoke resolves the *current* context. After
  // a reconnect, `context` is rebuilt; a captured reference would point at a
  // disposed context. eventa's native ws adapter installs wsDisconnectedEvent
  // and wsErrorEvent as abortOnEvents, so any invoke whose socket dies before
  // a response arrives rejects with a real Error instead of hanging.
  const invokeSendMessages = defineInvoke(getContext, sendMessages)
  const invokePullMessages = defineInvoke(getContext, pullMessages)

  return {
    status: () => mapStatus(ws.status.value, enabled.value, authenticated.value),
    connect() {
      if (enabled.value && ws.status.value === 'OPEN')
        return
      enabled.value = true
      // urlRef will recompute and useWebSocket reopens; if it was already
      // closed by a previous disconnect, call open() to nudge it.
      if (ws.status.value === 'CLOSED')
        ws.open()
    },
    disconnect() {
      // Flip intent off first so the autoReconnect loop won't fight us, then
      // ask VueUse to close. urlRef is now `undefined` which makes any
      // future automatic open() a no-op until connect() is called again.
      // Status watcher stays attached so callers can disconnect/connect on
      // the same handle.
      enabled.value = false
      activeSocket = undefined
      ws.close()
      disposeContext()
    },
    destroy() {
      enabled.value = false
      activeSocket = undefined
      ws.close()
      disposeContext()
      stopStatusWatch()
      stopTokenWatch()
    },
    sendMessages: req => invokeSendMessages(req),
    pullMessages: req => invokePullMessages(req),
    onNewMessages(handler) {
      newMessagesHandlers.add(handler)
      return () => {
        newMessagesHandlers.delete(handler)
      }
    },
    onStatusChange(handler) {
      statusHandlers.add(handler)
      return () => {
        statusHandlers.delete(handler)
      }
    },
  }
}
